import { useState } from 'react';
import { getPlayerAvatar } from '../../utils/player';
import GameDetailsModal from './GameDetailsModal';

/**
 * Renders a completed Classic/Doubles/Singles match: two teams with pairings and set/game scores.
 * Follows exact design system and color palette.
 */
const ClassicMatchCard = ({
  session,
  allPlayers,
  onDeleteSession,
  isArchived,
  canEdit,
}) => {
  const [showGameDetails, setShowGameDetails] = useState(false);
  const firstEntry = session?.sessionHistory?.[0];
  const isClassic = session?.sessionMode === 'classic' || session?.classicMatch || firstEntry?.classicMatch;

  if (!isClassic) {
    return null;
  }

  const matchSummary = session?.matchSummary || (firstEntry?.classicMatch ? firstEntry : null) || session;

  const toPlayerArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === 'object' && val !== null) {
      if (val.name || val.id || val.uid) return [val];
      const values = Object.values(val);
      if (values.length > 0) return values;
    }
    if (typeof val === 'string') return [val];
    return [];
  };

  const formatPlayerName = (p) => {
    if (!p) return '';
    if (typeof p === 'object') return p.name || p.id || p.uid || String(p);
    return String(p);
  };

  const team1Candidates = [
    toPlayerArray(matchSummary?.team1),
    toPlayerArray(matchSummary?.winningTeam),
    toPlayerArray(session?.team1),
    toPlayerArray(session?.winningTeam),
    toPlayerArray(session?.sessionPlayers).slice(0, 2),
    toPlayerArray(firstEntry?.winningTeam),
    toPlayerArray(firstEntry?.team1),
  ];

  const rawTeam1 = team1Candidates.find((arr) => arr.length > 0) || [];

  const team2Candidates = [
    toPlayerArray(matchSummary?.team2),
    toPlayerArray(matchSummary?.losingTeam),
    toPlayerArray(session?.team2),
    toPlayerArray(session?.losingTeam),
    toPlayerArray(session?.sessionPlayers).slice(2, 4),
    toPlayerArray(firstEntry?.losingTeam),
    toPlayerArray(firstEntry?.team2),
  ];

  const rawTeam2 = team2Candidates.find((arr) => arr.length > 0) || [];

  const team1 = rawTeam1.map(formatPlayerName).filter(Boolean);
  const team2 = rawTeam2.map(formatPlayerName).filter(Boolean);

  const rawSetScoresCandidates = [
    toPlayerArray(session?.setScores),
    toPlayerArray(matchSummary?.setScores),
    toPlayerArray(firstEntry?.setScores),
  ];

  const rawSetScores = rawSetScoresCandidates.find((arr) => arr.length > 0) || [];
  const setScores = rawSetScores.map((p) => {
    if (Array.isArray(p)) return [p[0] ?? 0, p[1] ?? 0];
    if (p && typeof p === 'object') return [p.team1 ?? p[0] ?? 0, p.team2 ?? p[1] ?? 0];
    return [0, 0];
  });

  if (setScores.length === 0) {
    setScores.push([0, 0]);
  }

  const hasWinningTeam = Boolean(
    (Array.isArray(matchSummary?.winningTeam) && matchSummary.winningTeam.length > 0) ||
    (Array.isArray(session?.winningTeam) && session.winningTeam.length > 0) ||
    (Array.isArray(firstEntry?.winningTeam) && firstEntry.winningTeam.length > 0) ||
    session?.mvp
  );
  const isUnfinished = session?.incomplete || session?.unfinished || firstEntry?.incomplete || firstEntry?.unfinished || !hasWinningTeam;

  const bestOfSets = session?.bestOfSets || matchSummary?.bestOfSets || (setScores.length > 1 ? 3 : 1);
  const numSetColumns = bestOfSets === 1 ? Math.max(setScores.length, 1) : Math.max(setScores.length, 3);

  const isSetWon = (v1, v2) => (v1 >= 6 && v1 - v2 >= 2) || v1 === 7;

  return (
    <div className="w-full flex flex-col justify-between p-0">
      <div>
        {/* The date already shows in the session navigator above, so only flag unfinished matches here (#54) */}
        {isUnfinished && (
          <div className="flex items-center mb-4">
            <span className="bg-slate-700/80 text-slate-300 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
              UNFINISHED
            </span>
          </div>
        )}

        {/* TEAM 1 & SET BLOCKS ROW */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            {(team1 || []).map((name) => (
              <div key={name} className="flex items-center gap-3">
                <img
                  src={getPlayerAvatar(allPlayers, name)}
                  alt={name}
                  className="avatar avatar-sm avatar-border"
                />
                <span className="font-extrabold text-slate-100 text-sm truncate">{name}</span>
              </div>
            ))}
          </div>

          {/* TEAM 1 SET SCORE BLOCKS */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {Array.from({ length: numSetColumns }, (_, i) => {
              const pair = setScores[i];
              if (!pair) {
                return (
                  <div
                    key={i}
                    className="w-9 h-11 rounded-[8px] bg-[#434e75] flex items-center justify-center font-bold text-lg text-black shadow-md"
                  >
                    -
                  </div>
                );
              }
              const t1Val = pair[0];
              const t2Val = pair[1];
              const t1Won = isSetWon(t1Val, t2Val);
              return (
                <div
                  key={i}
                  className={`w-9 h-11 flex items-center justify-center font-bold text-lg text-black shadow-md transition-all ${
                    t1Won
                      ? 'bg-[#f5b83d] rounded-[8px]'
                      : 'bg-[#a6c5ff] rounded-[8px]'
                  }`}
                >
                  {t1Val}
                </div>
              );
            })}
          </div>
        </div>

        <div
          className="my-3"
          style={{ height: 1, backgroundColor: 'rgba(255,255,255,0.12)' }}
        />

        {/* TEAM 2 & SET BLOCKS ROW */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col gap-2 flex-1 min-w-0">
            {(team2 || []).map((name) => (
              <div key={name} className="flex items-center gap-3">
                <img
                  src={getPlayerAvatar(allPlayers, name)}
                  alt={name}
                  className="avatar avatar-sm avatar-border"
                />
                <span className="font-extrabold text-slate-100 text-sm truncate">{name}</span>
              </div>
            ))}
          </div>

          {/* TEAM 2 SET SCORE BLOCKS */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {Array.from({ length: numSetColumns }, (_, i) => {
              const pair = setScores[i];
              if (!pair) {
                return (
                  <div
                    key={i}
                    className="w-9 h-11 rounded-[8px] bg-[#434e75] flex items-center justify-center font-bold text-lg text-black shadow-md"
                  >
                    -
                  </div>
                );
              }
              const t1Val = pair[0];
              const t2Val = pair[1];
              const t2Won = isSetWon(t2Val, t1Val);
              return (
                <div
                  key={i}
                  className={`w-9 h-11 flex items-center justify-center font-bold text-lg text-black shadow-md transition-all ${
                    t2Won
                      ? 'bg-[#f5b83d] rounded-[8px]'
                      : 'bg-[#a6c5ff] rounded-[8px]'
                  }`}
                >
                  {t2Val}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {isArchived && (
        <div className="flex flex-col gap-md mt-6">
          <button
            onClick={() => setShowGameDetails(true)}
            className="btn btn-secondary btn-full"
          >
            <span className="btn-text">GAME DETAILS</span>
          </button>
          {canEdit && (
            <button
              onClick={onDeleteSession}
              className="btn btn-danger btn-full"
            >
              <span className="btn-text">DELETE SESSION</span>
            </button>
          )}
        </div>
      )}

      {showGameDetails && (
        <GameDetailsModal
          session={session}
          allPlayers={allPlayers}
          onClose={() => setShowGameDetails(false)}
        />
      )}
    </div>
  );
};

export default ClassicMatchCard;
